import { useState, useContext } from "react";
import UserContext from "../../contexts/userContext";

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Typewriter from "../Typewriter";
import ProgressBar from 'react-bootstrap/ProgressBar'

const Spotlight = ({target}) => {
    const targetRect = target.getBoundingClientRect();
    const spotlightPadding = 15;
    const spotlightLeft = targetRect.x - spotlightPadding;
    const spotlightTop = targetRect.y - spotlightPadding;
    const spotlightWidth = targetRect.width + 2*spotlightPadding;
    const spotlightHeight = targetRect.height + 2*spotlightPadding;

    return (
        <div
            className="spotlight"
            style={{
                "display" : "block",
                "left" : `${spotlightLeft}px`,
                "top" : `${spotlightTop}px`,
                "width" : `${spotlightWidth}px`,
                "height" : `${spotlightHeight}px`
            }}
        />
    )
}

export const Tour = () => {
    const { userContext } = useContext(UserContext);
    const [ currentIndex, setCurrentIndex ] = useState(0);
    const [ spotlightIdx, setSpotlightIdx ] = useState(0);
    const [ forceRerenderCount, setForceRerenderCount ] = useState(0);
    const [ isTypewriterComplete, setIsTypewriterComplete ] = useState(true);

    const userName = (userContext.account) ? userContext.account.name : '';
    const steps = [
        {
            type:"modal",
            text:
                `Hello, ${userName}.~
                Thanks for joining me!~~
                I am an Analytics8 Chatbot, and I'd like to give you a quick tour of my functionality.`
        },{
            type:"modal",
            text:
                `Ask me a question or give me a command, and I can help you~
                _* Rewrite content for clarity or different audiences~^
                _* Sound more (or less) formal or technical~^
                _* Write personalized emails~^
                _* Plan and prepare for meetings~^
                _* Simplify or summarize information~^
                _* Proofread content and find contradictions~^
                _* Generate, explain, optimize, and translate code~^~
                I work best when you ask me questions in English, but I'm pretty good at answering in other languages or translating things.~~
                I can answer your questions based upon my general knowledge, but unlike ChatGPT, I can also use Analytics8 proprietary information to inform my replies.`,
        },{
            type:"modal",
            text:
                `Unlike ChatGPT and many versions of Microsoft Copilot, I am 100% dedicated to Analytics8.~~
                All information exchanged with me stays within the Analytics8 environment, so there is no risk of leaking sensitive information to outsiders.~~
                Said differently,~
                _* Your questions and commands~^
                _* My responses~^
                _* Analytics8 data and intellectual property~^
                will never leave the Analytics8 environment and will never be used to train somebody else's AI.`
        },{
            type:"spotlight",
            targetSelector:"#docCard0",
            text:
                `When you want to ask me a question or give me a command that does not require any knowledge of Analytics8 proprietary information, click this tile.~~
                In this situation, I'll just use my general knowledge to provide a response.`
        },{
            type:"spotlight",
            targetSelector:".a8-proprietary",
            text:
                `If you want to interact with me as if I was an Analytics8 employee, I'll need to supplement my general knowledge with Analytics8 proprietary information and data.~~
                Click any of these other tiles to specify what body of knowledge you'd like me to use during our conversation.`
        },{
            type:"spotlight",
            targetSelector:"#docCard0",
            clickTarget:"docCard0",
            text:
                `For the sake of this tour, I'll show you a few examples that I can handle with my general knowledge.~~
                Let's get started!`
        },{
            type:"spotlight",
            targetSelector:"#wizard",
            text:
                `When beginning a new conversation with me, I'll provide a few suggestions to get started. Click any of these tiles to engage with me.~~
                Over time, I'll get to know you better and prioritize my suggestions based upon your past interactions with me.`
        },{
            type:"spotlight",
            targetSelector:"#humanInputTextarea",
            targetValue:"What is the capital of Illinois?",
            clickTarget:"submitQueryButton",
            text:
                `Alternatively, you can always jump straight into a question or command in the bar at the bottom of the screen.~~
                This is also where you'll continue your conversation with me once we get started.~~
                Click the "Next" button, and I'll provide an example.`
        },{
            type:"spotlight",
            targetSelector:"#messageCard0",
            text:
                `After submitting a question or command, you will see your message on the right side of the window`
        },{
            type:"spotlight",
            targetSelector:"#messageCard1",
            text:
                `And my replies appear on the left side of the window`
        },{
            type:"spotlight",
            targetSelector:"#humanInputTextarea",
            targetValue:"How far is it from the most populated city in the state?",
            clickTarget:"submitQueryButton",
            text:
                `Now let's ask a follow-up question.~~
                Within a conversation, I have excellent memory! I use our conversation history as context when providing answers.`
        },{
            type:"spotlight",
            targetSelector:"#messageCard2",
            text:
                `Here is an example of a follow-up question that only makes sense if I remember what we've been talking about.~~
                In other words, you don't need to clarify "it" or "state" since I am able to draw upon our conversation history to fill in those contextual gaps.`
        },{
            type:"spotlight",
            targetSelector:"#messageCard3",
            text:
                `As you can see, I was able to answer your vaugely-worded question by using our conversation history as context.~~ 
                You didn't have to remind me that we were talking about Springfield or Illinois since I was able to gather that information from our conversation history.~~
                If you ever come back to this conversation later, we can pick up right where we left off - just as if there wasn't a break
                in our conversation at all!`
        },{
            type:"spotlight",
            targetSelector:"#conversationHistoryButton",
            clickTarget:"conversationHistoryButton",
            text:
                `You can see a list of all conversations we've ever had by clicking the ¤ button on the top-left of the navigation bar at the top of the screen.~~
                This is also where you can start a new conversation with me.`
        },{
            type:"spotlight",
            targetSelector:"#conversationHistory",
            text:
                `While humans are able to juggle multiple subjects within a single interaction,
                I'm most productive when focused on a single subject within a conversation.
                I assume that all prior information within a conversation is relevant to your most recent question or command.~~
                In other words, you may confuse me when mixing topics within a single conversation.
                This is why you should start a new conversation with me when you want to discuss another topic.~~
                But don't worry - you can pick a conversation back up at any time, and I'll remember exactly where we left off!`
        },{
            type:"spotlight",
            targetSelector:"#newConversationButton",
            clickTarget:"newConversationButton",
            text:
                `Since my memory is isolated to a single conversation that we have with each other,
                that also means I am unable to use anything that we discuss in conversations with other users.~~
                Anything we discuss is specific to you, specific to a single conversation.~~
                In other words, everything we discuss is private!~^~
                Let's start a new conversation so I can show you what I mean.`
        },{
            type:"spotlight",
            targetSelector:"#humanInputTextarea",
            targetValue:"What is its population?",
            clickTarget:"submitQueryButton",
            text:
                `Let's ask something that would make sense in our prior conversation but will not make sense in our new conversation.`
        },{
            type:"spotlight",
            targetSelector:"#messageCard0",
            text:
                `Here you asked me about "its" population...`
        },{
            type:"spotlight",
            targetSelector:"#messageCard1",
            text:
                `... but I am unable to answer this question because I don't have context about "its" from our other conversation.~~
                This should give you comfort that anything we discuss is private to us.
                I can't even remember what you and I discussed in other conversations, so I certainly can't reference conversations with other people when chatting with you!`
        },{
            type:"spotlight",
            targetSelector:"#homeButton",
            clickTarget:"homeButton",
            text:
                `Let's go back home now by clicking the Å button on the top-left of the navigation bar at the top of the screen`
        },{
            type:"spotlight",
            targetSelector:"#helpButton",
            clickTarget:"helpButton",
            text:
                `It was good to briefly get to know each other, but please understand there's a lot more that I can do to help you.~~
                Please take a moment to browse the Help menu. This helpful resource is always available by clicking the ¿ button on the top-right of the navigation bar!`
        }
    ];

    const curStep = steps[currentIndex];

    const updateIndex = () => {
        if (curStep === undefined) {
            return;
        }
        
        const target = document.querySelector(curStep.targetSelector);
        const targetValue = curStep.targetValue;
        if (target && targetValue) {
            target.value = targetValue;
        }

        const clickTarget = document.getElementById(curStep.clickTarget);
        if (clickTarget) {
            clickTarget.click();
        }

        setCurrentIndex(currentIndex+1);
        
        if (spotlightIdx !== 0) {
            setSpotlightIdx(0);
        }
    };

    const showSpotlight = (curStep) => {
        const targets = document.querySelectorAll(curStep.targetSelector);
        const target = targets[spotlightIdx];

        if (target === undefined) {
            if (spotlightIdx !== 0) {
                setSpotlightIdx(0);
            }
            return;
        }

        if (targets.length > 1) {
            setTimeout(() => {
                setSpotlightIdx(spotlightIdx + 1);;
            }, 1500);
        } 
        
        return <Spotlight target={target} />
    };

    const renderTour = () => {
        if (curStep === undefined) {
            return;
        }

        const isSpotlight = curStep.type === 'spotlight';
        
        return (<>
            {isSpotlight && showSpotlight(curStep)}

            <Modal
                show={true}
                backdrop={isSpotlight ? false : true}
                size={isSpotlight ? "lg" : "lg"}
            >
                <Modal.Body>
                    <Typewriter
                        id={`tourIndex${currentIndex}`}
                        text={curStep.text}
                        delay={20}
                        pauseDelay={200}
                        isComplete={isTypewriterComplete}
                        setIsComplete={setIsTypewriterComplete}
                    />
                    
                </Modal.Body>
                
                <Modal.Footer style={{"justifyContent":"space-between"}}>
                    <ProgressBar now={(currentIndex+1)/steps.length*100} style={{"width":"75%"}} />
                    <Button
                        variant="primary"
                        onClick={updateIndex}
                        className="a8-blue-background mx-2"
                        disabled={!isTypewriterComplete}
                    >
                        Next <i className="bi-arrow-right" />
                    </Button>
                </Modal.Footer>
            </Modal>
        </>)
    }

    const doTour = userContext.tour;
    if (!doTour) {
        return;
    }
    
    if (curStep?.targetSelector) {
        const spotlightTarget = document.querySelector(curStep.targetSelector);
        if (spotlightTarget == null) {
            setTimeout(() => {
                setForceRerenderCount(forceRerenderCount+1);
            }, 1000);
        } else {
            return renderTour();
        }
    } else {
        return renderTour();
    }
}